import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Radio, UserPlus, Moon, Pause, Play, Palette, Brush, Eraser, Send, Download, Film, ChevronUp, ChevronDown, Check, Users as UsersIcon, Copy } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import LiveBroadcaster from "./LiveBroadcaster";

/* ============================================================
 * JR PODCAST STUDIO — 3-step simplified live stream for kids
 *   Step 1: Title + topic
 *   Step 2: Invite up to 3 friends (parent-permission required)
 *   Step 3: Go LIVE
 * ============================================================ */
export function JrPodcastStudio({ onClose }) {
  const [step, setStep] = useState(1);
  const [session, setSession] = useState(null);
  const [title, setTitle] = useState("My Podcast");
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [live, setLive] = useState(false);
  const [streamId, setStreamId] = useState(null);
  const [copied, setCopied] = useState(false);

  const createSession = async () => {
    if (!title.trim()) { toast.error("Give your show a name!"); return; }
    setBusy(true);
    try {
      const r = await api.post("/kids/jr-studio/session", { title: title.trim(), topic: topic.trim() });
      setSession(r.data); setStep(2);
      toast.success("Your show is ready! Invite up to 3 friends.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const invite = async () => {
    if (!inviteEmail.trim()) { toast.error("Ask a grown-up for the email!"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/kids/jr-studio/${session.id}/invite`, {
        friend_email: inviteEmail.trim(), friend_name: inviteName.trim() || null,
      });
      setSession((s) => ({ ...s, invites: [...(s.invites || []), { token: r.data.token, email: inviteEmail, name: inviteName, approved_by: null }] }));
      setInviteEmail(""); setInviteName("");
      toast.success("Invite sent! Their grown-up needs to approve.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const removeInvite = (tok) => setSession((s) => ({ ...s, invites: (s.invites || []).filter((i) => i.token !== tok) }));

  const goLive = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/kids/jr-studio/${session.id}/go-live`, {});
      setLive(true); setStep(3);
      setStreamId(r.data.stream_id);
      toast.success("You're LIVE! 🎉");
      // Copy friendly share link
      try { await navigator.clipboard?.writeText(`${window.location.origin}/kids/live/${r.data.stream_id}`); setCopied(true); } catch {}
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const endShow = async () => {
    try {
      await api.post(`/kids/jr-studio/${session.id}/end`, {});
      setLive(false);
      toast.success("Great show! 🎊");
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const Step = ({ n, label, active }) => (
    <div className={`flex items-center gap-[1vmin] ${active ? "text-white" : "text-white/40"}`}>
      <div className={`w-[5vmin] h-[5vmin] min-w-[36px] min-h-[36px] rounded-full flex items-center justify-center font-black text-[2vmin] ${active ? "bg-yellow-300 text-[#222]" : "bg-white/20"}`}>{n}</div>
      <span className="font-display font-black text-[1.8vmin] hidden md:inline">{label}</span>
    </div>
  );

  return (
    <div data-testid="kids-jr-studio" className="fixed inset-0 z-50 flex flex-col items-center p-[3vmin] overflow-y-auto text-white" style={{
      background: "radial-gradient(ellipse at 20% 0%, #ec4899 0%, transparent 55%), radial-gradient(ellipse at 80% 100%, #22d3ee 0%, transparent 55%), linear-gradient(180deg, #1e1b4b 0%, #2a1d6d 60%, #0b1336 100%)"
    }}>
      {/* Top */}
      <div className="w-full max-w-5xl flex items-center justify-between">
        <div className="flex items-center gap-[1.2vmin]">
          <Radio className="w-[3vmin] h-[3vmin] min-w-[24px] text-pink-300" />
          <div>
            <div className="text-[1.4vmin] uppercase tracking-[0.28em] font-black text-pink-300">JR PODCAST STUDIO</div>
            <div className="font-display font-black text-[3vmin]">Let's make a show! 🎙️</div>
          </div>
        </div>
        <button data-tv-focus="true" data-testid="jr-studio-close" onClick={onClose} className="tv-focusable kids-focusable w-[5vmin] h-[5vmin] min-w-[42px] min-h-[42px] rounded-full bg-white/90 text-[#222] flex items-center justify-center shadow-lg">
          <X className="w-[2.4vmin] h-[2.4vmin] min-w-[18px]" />
        </button>
      </div>

      {/* Stepper */}
      <div className="w-full max-w-5xl flex items-center justify-center gap-[3vmin] my-[3vmin]">
        <Step n={1} label="Name it" active={step >= 1} />
        <div className="h-1 w-[6vmin] bg-white/20 rounded-full overflow-hidden"><div className="h-full bg-yellow-300 transition-all" style={{ width: step >= 2 ? "100%" : "0%" }} /></div>
        <Step n={2} label="Invite friends" active={step >= 2} />
        <div className="h-1 w-[6vmin] bg-white/20 rounded-full overflow-hidden"><div className="h-full bg-yellow-300 transition-all" style={{ width: step >= 3 ? "100%" : "0%" }} /></div>
        <Step n={3} label="Go LIVE" active={step >= 3} />
      </div>

      {/* Step 1 */}
      {step === 1 && (
        <div className="w-full max-w-3xl bg-white/10 backdrop-blur ring-2 ring-white/15 rounded-[2rem] p-[3vmin] shadow-2xl space-y-[1.6vmin]">
          <div className="text-[2vmin] font-black">What's your show called?</div>
          <input data-testid="jr-title" value={title} onChange={(e) => setTitle(e.target.value.slice(0, 60))}
            className="w-full bg-white/15 border-2 border-white/20 focus:border-pink-400 rounded-2xl px-[2vmin] py-[1.4vmin] text-[2.4vmin] font-display font-black outline-none" placeholder="e.g. Zi Zi Dance Time"
          />
          <div className="text-[1.8vmin] font-black mt-[1vmin]">What's it about? <span className="text-white/50">(optional)</span></div>
          <input data-testid="jr-topic" value={topic} onChange={(e) => setTopic(e.target.value.slice(0, 160))}
            className="w-full bg-white/15 border-2 border-white/20 focus:border-pink-400 rounded-2xl px-[2vmin] py-[1.2vmin] text-[1.8vmin] outline-none" placeholder="Today's topic…"
          />
          <div className="flex justify-end pt-[1vmin]">
            <button data-tv-focus="true" data-testid="jr-step1-next" onClick={createSession} disabled={busy}
              className="tv-focusable kids-focusable inline-flex items-center gap-[1vmin] bg-gradient-to-r from-pink-500 to-fuchsia-500 hover:from-pink-400 hover:to-fuchsia-400 font-black px-[3vmin] py-[1.4vmin] rounded-full text-[2vmin] shadow-xl disabled:opacity-60">
              Next →
            </button>
          </div>
        </div>
      )}

      {/* Step 2 */}
      {step === 2 && session && (
        <div className="w-full max-w-3xl bg-white/10 backdrop-blur ring-2 ring-white/15 rounded-[2rem] p-[3vmin] shadow-2xl space-y-[1.6vmin]">
          <div className="text-[2vmin] font-black">Invite up to 3 friends 🎉</div>
          <div className="text-[1.5vmin] text-white/70">Their grown-up needs to say yes before they can join. We'll email them a safe approval link.</div>

          <div className="flex flex-col md:flex-row gap-[1vmin]">
            <input data-testid="jr-invite-name" value={inviteName} onChange={(e) => setInviteName(e.target.value.slice(0, 40))}
              placeholder="Friend's name" className="flex-1 bg-white/15 border-2 border-white/20 focus:border-pink-400 rounded-2xl px-[1.6vmin] py-[1vmin] text-[1.8vmin] outline-none" />
            <input data-testid="jr-invite-email" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value.slice(0, 120))}
              placeholder="Grown-up's email" className="flex-[2] bg-white/15 border-2 border-white/20 focus:border-pink-400 rounded-2xl px-[1.6vmin] py-[1vmin] text-[1.8vmin] outline-none" />
            <button data-tv-focus="true" data-testid="jr-invite-btn" onClick={invite} disabled={busy || (session.invites?.length || 0) >= 3}
              className="tv-focusable kids-focusable inline-flex items-center gap-[1vmin] bg-cyan-400 hover:bg-cyan-300 text-[#0a1020] font-black px-[2.2vmin] py-[1vmin] rounded-full text-[1.8vmin] shadow-xl disabled:opacity-50"
            ><UserPlus className="w-[2vmin] h-[2vmin] min-w-[16px]" /> Invite</button>
          </div>

          <div className="space-y-[0.8vmin]">
            {(session.invites || []).map((inv) => (
              <div key={inv.token} data-testid={`jr-invite-row-${inv.token}`} className="flex items-center gap-[1vmin] bg-black/30 rounded-xl px-[1.4vmin] py-[1vmin]">
                <div className="text-[2.4vmin]">🧒</div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-[1.6vmin] truncate">{inv.name || inv.email}</div>
                  <div className="text-white/50 text-[1.3vmin] truncate">{inv.email}</div>
                </div>
                {inv.approved_by
                  ? <span className="inline-flex items-center gap-1 bg-emerald-500/20 text-emerald-300 font-black text-[1.3vmin] px-[1vmin] py-[0.4vmin] rounded-full"><Check className="w-3 h-3" /> Approved</span>
                  : <span className="bg-amber-500/20 text-amber-300 font-black text-[1.3vmin] px-[1vmin] py-[0.4vmin] rounded-full">Waiting for grown-up</span>
                }
                <button onClick={() => removeInvite(inv.token)} className="text-white/50 hover:text-red-300 p-[0.4vmin]"><X className="w-[1.8vmin] h-[1.8vmin] min-w-[14px]" /></button>
              </div>
            ))}
            {(!session.invites || session.invites.length === 0) && (
              <div className="text-white/45 text-[1.5vmin] italic">No friends invited yet — or skip this step to go solo.</div>
            )}
          </div>

          <div className="flex items-center justify-between pt-[1vmin]">
            <button data-tv-focus="true" onClick={() => setStep(1)}
              className="tv-focusable kids-focusable text-white/70 hover:text-white font-bold text-[1.6vmin]">← Back</button>
            <button data-tv-focus="true" data-testid="jr-step2-next" onClick={() => setStep(3)}
              className="tv-focusable kids-focusable inline-flex items-center gap-[1vmin] bg-gradient-to-r from-yellow-300 to-amber-400 text-[#222] font-black px-[3vmin] py-[1.4vmin] rounded-full text-[2vmin] shadow-xl">Next →</button>
          </div>
        </div>
      )}

      {/* Step 3 — Go Live */}
      {step === 3 && session && (
        <div className="w-full max-w-5xl bg-white/10 backdrop-blur ring-2 ring-white/15 rounded-[2rem] p-[2vmin] shadow-2xl">
          {!live ? (
            <div className="text-center p-[2vmin]">
              <div className="text-[8vmin] animate-pulse">🎙️</div>
              <div className="font-display font-black text-[3.2vmin] mb-[0.6vmin]">Ready?</div>
              <div className="text-white/70 text-[1.8vmin] mb-[2vmin]">"{session.title}" will go live under Tap IN Kids. Chat is off by default.</div>
              <button data-tv-focus="true" data-testid="jr-go-live" onClick={goLive} disabled={busy}
                className="tv-focusable kids-focusable inline-flex items-center gap-[1vmin] bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-400 hover:to-pink-400 font-black px-[4vmin] py-[1.8vmin] rounded-full text-[2.4vmin] shadow-2xl disabled:opacity-60">
                <Radio className="w-[2.6vmin] h-[2.6vmin] min-w-[20px]" /> {busy ? "Starting…" : "GO LIVE! 🎉"}
              </button>
              <div className="mt-[1.4vmin]">
                <button data-tv-focus="true" onClick={() => setStep(2)} className="tv-focusable kids-focusable text-white/70 hover:text-white font-bold text-[1.6vmin]">← Back</button>
              </div>
            </div>
          ) : (
            <div data-testid="jr-live-stage" className="space-y-[1.4vmin]">
              <div className="flex items-center justify-between flex-wrap gap-[1vmin] px-[1vmin]">
                <div className="flex items-center gap-[1vmin]">
                  <div className="inline-flex items-center gap-2 bg-red-600 text-white font-black text-[1.2vmin] uppercase tracking-[0.25em] px-[1.2vmin] py-[0.4vmin] rounded-full animate-pulse">
                    <span className="w-2 h-2 rounded-full bg-white" /> LIVE
                  </div>
                  <div className="font-display font-black text-[2.4vmin]">{session.title}</div>
                </div>
                <div className="flex items-center gap-[0.8vmin]">
                  {streamId && (
                    <a
                      href={`/kids/live/${streamId}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="jr-share-link"
                      className="tv-focusable kids-focusable inline-flex items-center gap-1 bg-white/15 hover:bg-white/25 text-white font-black px-[1.6vmin] py-[0.8vmin] rounded-full text-[1.3vmin]"
                    ><Copy className="w-3 h-3" /> {copied ? "Link copied" : "Share link"}</a>
                  )}
                  <button data-tv-focus="true" data-testid="jr-end-show" onClick={endShow}
                    className="tv-focusable kids-focusable bg-white text-[#222] font-black px-[1.8vmin] py-[0.8vmin] rounded-full text-[1.3vmin] shadow-xl">End show</button>
                </div>
              </div>
              {/* Real WebRTC broadcaster — same infrastructure grown-up creators
                  use, just wrapped in kids branding. Host sees live camera
                  preview + viewer list; chat hidden per kids-safe defaults. */}
              {streamId && (
                <div className="rounded-[1.2rem] overflow-hidden ring-2 ring-white/20 bg-black">
                  <LiveBroadcaster
                    streamId={streamId}
                    kidsBrand={true}
                    hideChat={true}
                    hideCrewBoost={true}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ============================================================
 * DRAW-AND-SHARE CANVAS
 * ============================================================ */
export function KidsDraw({ onClose }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [color, setColor] = useState("#ec4899");
  const [size, setSize] = useState(10);
  const [tool, setTool] = useState("brush"); // brush | eraser
  const [sending, setSending] = useState(false);

  const COLORS = ["#ec4899","#f97316","#facc15","#22c55e","#06b6d4","#3b82f6","#8b5cf6","#0b1336","#ffffff"];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, rect.width, rect.height);
      ctx.lineCap = "round"; ctx.lineJoin = "round";
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const posFromEvent = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const p = e.touches?.[0] || e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };
  const start = (e) => { drawing.current = true; const { x, y } = posFromEvent(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const { x, y } = posFromEvent(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = tool === "eraser" ? "#ffffff" : color;
    ctx.lineWidth = tool === "eraser" ? size * 2 : size;
    ctx.lineTo(x, y); ctx.stroke();
  };
  const end = () => { drawing.current = false; };
  const clear = () => {
    const canvas = canvasRef.current; const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, rect.width, rect.height);
  };
  const download = () => {
    const url = canvasRef.current.toDataURL("image/png");
    const a = document.createElement("a"); a.href = url; a.download = "my-drawing.png"; a.click();
  };
  const share = async () => {
    setSending(true);
    try {
      const url = canvasRef.current.toDataURL("image/png");
      await api.post("/kids/draw/share", { data_url: url, title: "My drawing" });
      toast.success("Sent to your grown-up! 💖");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="kids-draw-overlay" className="fixed inset-0 z-50 flex flex-col items-center p-[2.4vmin] text-white" style={{ background: "radial-gradient(ellipse at 80% 10%, #22d3ee 0%, transparent 55%), radial-gradient(ellipse at 10% 90%, #ec4899 0%, transparent 55%), linear-gradient(180deg, #0b1336 0%, #1e1b4b 100%)" }}>
      <div className="w-full max-w-6xl flex items-center justify-between mb-[1vmin]">
        <div className="flex items-center gap-[1.2vmin]">
          <Palette className="w-[3vmin] h-[3vmin] min-w-[22px] text-pink-300" />
          <div className="font-display font-black text-[2.6vmin]">Draw &amp; share 🎨</div>
        </div>
        <button data-tv-focus="true" data-testid="draw-close" onClick={onClose} className="tv-focusable kids-focusable w-[5vmin] h-[5vmin] min-w-[42px] min-h-[42px] rounded-full bg-white/90 text-[#222] flex items-center justify-center shadow-lg"><X className="w-[2.4vmin] h-[2.4vmin] min-w-[18px]" /></button>
      </div>

      <div className="w-full max-w-6xl grid grid-cols-[auto_1fr] gap-[1.4vmin]">
        {/* Tool rail */}
        <div className="flex flex-col gap-[1vmin] items-center bg-white/10 rounded-[1.4rem] p-[1vmin] backdrop-blur ring-1 ring-white/10">
          <button data-tv-focus="true" data-testid="draw-brush" onClick={() => setTool("brush")} className={`tv-focusable kids-focusable w-[5vmin] h-[5vmin] min-w-[42px] min-h-[42px] rounded-xl flex items-center justify-center ${tool === "brush" ? "bg-pink-400 text-white" : "bg-white/10 text-white hover:bg-white/20"}`}><Brush className="w-[2.4vmin] h-[2.4vmin] min-w-[18px]" /></button>
          <button data-tv-focus="true" data-testid="draw-eraser" onClick={() => setTool("eraser")} className={`tv-focusable kids-focusable w-[5vmin] h-[5vmin] min-w-[42px] min-h-[42px] rounded-xl flex items-center justify-center ${tool === "eraser" ? "bg-white text-[#222]" : "bg-white/10 text-white hover:bg-white/20"}`}><Eraser className="w-[2.4vmin] h-[2.4vmin] min-w-[18px]" /></button>
          <div className="h-[1px] w-full bg-white/10 my-[0.4vmin]" />
          {COLORS.map((c) => (
            <button key={c} data-tv-focus="true" data-testid={`draw-color-${c}`} onClick={() => { setColor(c); setTool("brush"); }}
              className={`tv-focusable kids-focusable w-[4vmin] h-[4vmin] min-w-[34px] min-h-[34px] rounded-full ring-2 ${color === c ? "ring-white scale-110" : "ring-white/20"} transition shadow`}
              style={{ background: c }} />
          ))}
          <div className="h-[1px] w-full bg-white/10 my-[0.4vmin]" />
          {[6, 12, 20].map((s) => (
            <button key={s} data-tv-focus="true" onClick={() => setSize(s)} className={`tv-focusable kids-focusable w-[5vmin] h-[5vmin] min-w-[42px] min-h-[42px] rounded-xl flex items-center justify-center ${size === s ? "bg-yellow-300 text-[#222]" : "bg-white/10 text-white"}`}>
              <span className="block rounded-full" style={{ width: s, height: s, background: "currentColor" }} />
            </button>
          ))}
        </div>

        {/* Canvas */}
        <div className="bg-white rounded-[2rem] shadow-2xl overflow-hidden ring-4 ring-white/40 flex flex-col">
          <canvas ref={canvasRef} data-testid="draw-canvas" className="w-full flex-1 touch-none cursor-crosshair"
            style={{ height: "60vmin" }}
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          />
        </div>
      </div>

      <div className="w-full max-w-6xl flex flex-wrap justify-end gap-[1vmin] mt-[1.4vmin]">
        <button data-tv-focus="true" data-testid="draw-clear" onClick={clear} className="tv-focusable kids-focusable bg-white/15 hover:bg-white/25 font-black px-[2.2vmin] py-[1vmin] rounded-full text-[1.6vmin]">Clear</button>
        <button data-tv-focus="true" data-testid="draw-download" onClick={download} className="tv-focusable kids-focusable inline-flex items-center gap-1 bg-white/15 hover:bg-white/25 font-black px-[2.2vmin] py-[1vmin] rounded-full text-[1.6vmin]"><Download className="w-[1.8vmin] h-[1.8vmin] min-w-[14px]" /> Save</button>
        <button data-tv-focus="true" data-testid="draw-share" onClick={share} disabled={sending} className="tv-focusable kids-focusable inline-flex items-center gap-1 bg-gradient-to-r from-pink-500 to-fuchsia-500 font-black px-[2.4vmin] py-[1vmin] rounded-full text-[1.8vmin] disabled:opacity-50 shadow-xl"><Send className="w-[1.8vmin] h-[1.8vmin] min-w-[14px]" /> {sending ? "Sending…" : "Send to grown-up"}</button>
      </div>
    </div>
  );
}

/* ============================================================
 * BEDTIME STORY / LULLABY PLAYER
 * ============================================================ */
export function KidsBedtime({ onClose }) {
  const [tracks, setTracks] = useState([]);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  useEffect(() => {
    api.get("/kids/bedtime/tracks").then((r) => setTracks(r.data?.tracks || [])).catch(() => {});
  }, []);
  const t = tracks[idx];
  const toggle = () => {
    const a = audioRef.current; if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play().then(() => setPlaying(true)).catch(() => toast.error("Tap again to play")); }
  };
  const pick = (i) => { setIdx(i); setPlaying(false); setTimeout(() => { audioRef.current?.play?.().then(() => setPlaying(true)).catch(() => {}); }, 60); };

  return (
    <div data-testid="kids-bedtime-overlay" className="fixed inset-0 z-50 flex flex-col items-center p-[3vmin] text-white overflow-hidden" style={{
      background: "radial-gradient(ellipse at 80% 12%, #facc15 0%, transparent 18%), radial-gradient(ellipse at 50% 80%, #6366f1 0%, transparent 50%), linear-gradient(180deg, #0b1336 0%, #1e1b4b 55%, #3b0764 100%)"
    }}>
      <div aria-hidden className="pointer-events-none absolute inset-0 kids-stars-dense" />
      {/* Moon */}
      <div aria-hidden className="pointer-events-none absolute z-0 rounded-full" style={{
        top: "6%", right: "8%", width: "clamp(110px, 18vmin, 220px)", height: "clamp(110px, 18vmin, 220px)",
        background: "radial-gradient(circle at 35% 35%, #fff9dc 0%, #fff1b0 45%, #ffd56a 80%, transparent 86%)",
        boxShadow: "0 0 80px 20px rgba(255,231,150,0.5), 0 0 140px 40px rgba(255,231,150,0.28)",
      }} />

      <div className="relative z-10 w-full max-w-4xl flex items-center justify-between">
        <div className="flex items-center gap-[1vmin]">
          <Moon className="w-[3vmin] h-[3vmin] min-w-[22px] text-yellow-200" />
          <div className="font-display font-black text-[2.4vmin]">Bedtime Lullabies 🌙</div>
        </div>
        <button data-tv-focus="true" data-testid="bedtime-close" onClick={onClose} className="tv-focusable kids-focusable w-[5vmin] h-[5vmin] min-w-[42px] min-h-[42px] rounded-full bg-white/90 text-[#222] flex items-center justify-center shadow-lg"><X className="w-[2.4vmin] h-[2.4vmin] min-w-[18px]" /></button>
      </div>

      {t && (
        <div className="relative z-10 mt-[2vmin] w-full max-w-4xl text-center">
          <div className={`inline-block rounded-full bg-gradient-to-br ${t.color} p-[3vmin] shadow-2xl`}>
            <div className="text-[16vmin] leading-none">{t.emoji}</div>
          </div>
          <div className="font-display font-black text-[4vmin] mt-[2vmin]">{t.title}</div>
          <div className="text-white/70 text-[1.8vmin]">{Math.round((t.duration_sec || 0) / 60)} min</div>
          <button data-tv-focus="true" data-testid="bedtime-toggle" onClick={toggle}
            className="tv-focusable kids-focusable mt-[2vmin] inline-flex items-center gap-[1vmin] bg-yellow-300 hover:bg-yellow-200 text-[#222] font-black px-[3vmin] py-[1.6vmin] rounded-full text-[2.4vmin] shadow-2xl">
            {playing ? <Pause className="w-[2.6vmin] h-[2.6vmin] min-w-[20px]" /> : <Play className="w-[2.6vmin] h-[2.6vmin] min-w-[20px] fill-[#222]" />}
            {playing ? "Pause" : "Play"}
          </button>
          <audio ref={audioRef} src={t.audio_url} loop onEnded={() => setPlaying(false)} />
        </div>
      )}

      {/* Track picker */}
      <div className="relative z-10 mt-[3vmin] w-full max-w-4xl grid grid-cols-2 md:grid-cols-4 gap-[1.4vmin]">
        {tracks.map((tr, i) => (
          <button key={tr.id} data-tv-focus="true" data-testid={`bedtime-track-${tr.id}`} onClick={() => pick(i)}
            className={`tv-focusable kids-focusable rounded-[1.4rem] p-[1.4vmin] font-display font-black text-[1.8vmin] flex items-center gap-[1vmin] ${i === idx ? "bg-yellow-300 text-[#222]" : "bg-white/10 hover:bg-white/20 text-white"}`}>
            <span className="text-[3vmin]">{tr.emoji}</span>
            <span className="flex-1 text-left truncate">{tr.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
 * KIDS SHORTS — vertical scrollable shorts feed, pre-approved
 * ============================================================ */
export function KidsShorts({ onClose }) {
  const [items, setItems] = useState([]);
  const [i, setI] = useState(0);
  const videoRef = useRef(null);

  useEffect(() => {
    api.get("/kids/shorts?limit=30").then((r) => setItems(r.data?.shorts || [])).catch(() => {});
  }, []);
  useEffect(() => {
    const kh = (e) => {
      if (e.key === "ArrowUp") setI((n) => Math.max(0, n - 1));
      else if (e.key === "ArrowDown") setI((n) => Math.min(items.length - 1, n + 1));
      else if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", kh);
    return () => window.removeEventListener("keydown", kh);
  }, [items.length, onClose]);
  const v = items[i];

  const src = v && (v.hls_url || v.embed_url || v.video_url);
  const isEmbed = src && /youtube|youtu\.be|vimeo|tiktok/i.test(src);

  return (
    <div data-testid="kids-shorts-overlay" className="fixed inset-0 z-50 flex items-center justify-center bg-black">
      <div className="absolute top-[2vmin] right-[2vmin] z-20 flex gap-[1vmin]">
        <button data-tv-focus="true" data-testid="shorts-close" onClick={onClose} className="tv-focusable kids-focusable w-[5vmin] h-[5vmin] min-w-[42px] min-h-[42px] rounded-full bg-white/90 text-[#222] flex items-center justify-center shadow-lg"><X className="w-[2.4vmin] h-[2.4vmin] min-w-[18px]" /></button>
      </div>
      <div className="absolute right-[2vmin] top-1/2 -translate-y-1/2 z-20 flex flex-col gap-[1vmin]">
        <button data-tv-focus="true" data-testid="shorts-up" onClick={() => setI((n) => Math.max(0, n - 1))} className="tv-focusable kids-focusable w-[5vmin] h-[5vmin] min-w-[42px] min-h-[42px] rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white"><ChevronUp className="w-[3vmin] h-[3vmin] min-w-[22px]" /></button>
        <button data-tv-focus="true" data-testid="shorts-down" onClick={() => setI((n) => Math.min(items.length - 1, n + 1))} className="tv-focusable kids-focusable w-[5vmin] h-[5vmin] min-w-[42px] min-h-[42px] rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white"><ChevronDown className="w-[3vmin] h-[3vmin] min-w-[22px]" /></button>
      </div>

      <div className="relative w-full h-full max-w-[420px] md:max-w-[560px] mx-auto bg-black">
        {v && (
          isEmbed ? (
            <iframe key={v.id} src={src} className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen />
          ) : (
            <video key={v.id} ref={videoRef} src={src} autoPlay loop playsInline controls className="w-full h-full object-cover" />
          )
        )}
        {v && (
          <div className="absolute bottom-[2vmin] left-[2vmin] right-[9vmin] text-white">
            <div className="font-display font-black text-[2.2vmin] drop-shadow">{v.title}</div>
            <div className="text-white/80 text-[1.6vmin] drop-shadow">@{v.handle || v.creator_name}</div>
            <div className="mt-1 text-xs uppercase tracking-[0.2em] font-black bg-emerald-500/80 inline-block px-2 py-0.5 rounded">KID APPROVED ✓</div>
          </div>
        )}
        {!v && (
          <div className="absolute inset-0 flex items-center justify-center text-white/70">No shorts yet — check back soon!</div>
        )}
      </div>
    </div>
  );
}
